import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { enqueueSnackbar } from "notistack";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { TOAST } from "@/shared/constants/toastMessages";
import { useDeceasedSSNData } from "@/shared/hooks/useDeceasedSSNData";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useRegularSSNData } from "@/shared/hooks/useRegularSSNData";
import { useSaveSSN } from "@/shared/hooks/useSaveSSN";
import { useUpdateUser } from "@/shared/hooks/useUpdateUser";
import { useUserRelationship } from "@/shared/hooks/useUserRelationship";
import { useUserSettingsData } from "@/shared/hooks/useUserSettingsData";
import { useUserIdExtraction } from "@/shared/hooks/useUserIdExtraction";
import { supabase } from "@/shared/lib/supabase";
import { US_STATES } from "@/shared/constants/constants";
import { buildAddress, extractAddressFromPlace } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { TaskActionLogger } from "@/shared/utils/taskActionLogger";
import { isValidSSN, stripPhone } from "@/shared/utils/formatters";
import { ELAYNE_TASK_IDS } from "@/shared/constants/constants";
import { logActivity } from "@/shared/utils/activityLogger";
import { notifyTaskSubmitted } from "@/shared/utils/slackNotifications";
import { useNotificationFlags } from "@/shared/hooks/useNotificationFlags";
import { trackEvent, EventType } from "@/shared/utils/core";
import { resolveFieldValue, type ExtraFieldSources } from "@/shared/utils/essentialsFieldSources";

import type { EssentialModule, FormField } from "@/shared/types/estateEssentials";

export type FormViewState = "editing" | "reviewing" | "submitted";

export function useInlineFormState(module: EssentialModule) {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const deceasedData = useDeceasedUserData();
  const userData = useUserSettingsData();
  const idData = useUserIdExtraction();
  const { deceasedSSN, isLoading: isDeceasedSSNLoading } = useDeceasedSSNData();
  const { ssn: userSSN, isLoading: isUserSSNLoading } = useRegularSSNData();
  const { relationship, isLoading: isRelationshipLoading } = useUserRelationship();
  const { saveSSN: saveDeceasedSSN } = useSaveSSN();
  const { updateUserAsync } = useUpdateUser();
  const { canSendNotification } = useNotificationFlags();
  const queryClient = useQueryClient();

  const taskId = module.taskId ?? "";
  const fields = useMemo(() => module.formFields ?? [], [module.formFields]);

  const extraSources: ExtraFieldSources = useMemo(
    () => ({ relationship, deceasedSSN, userSSN }),
    [relationship, deceasedSSN, userSSN],
  );

  const { data: estateTask, isLoading: isTaskLoading } = useQuery({
    queryKey: ["estateTask", estateId, taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("id, task_id, status, answers")
        .eq("estate_id", estateId!)
        .eq("task_id", taskId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!estateId && !!taskId,
  });

  const [userEdits, setUserEdits] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [viewState, setViewState] = useState<FormViewState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ssnVisible, setSSNVisible] = useState<Record<string, boolean>>({});
  const [showTPA, setShowTPA] = useState(false);

  const resolvedValues = useMemo(() => {
    const values: Record<string, string> = {};
    for (const field of fields) {
      values[field.key] = resolveFieldValue(
        field.autoPopulateKey,
        deceasedData,
        userData,
        idData,
        extraSources,
      );
    }
    return values;
  }, [fields, deceasedData, userData, idData, extraSources]);

  const formValues = useMemo(() => {
    const merged: Record<string, string> = {};
    for (const field of fields) {
      merged[field.key] = resolvedValues[field.key] ?? "";
    }
    return { ...merged, ...userEdits };
  }, [fields, resolvedValues, userEdits]);

  const setFormValues = useCallback(
    (
      updater: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
    ) => {
      if (typeof updater === "function") {
        setUserEdits((prev) => {
          const result = updater(prev);
          return result;
        });
      } else {
        setUserEdits(updater);
      }
    },
    [],
  );

  const [isRefreshing, setIsRefreshing] = useState(true);

  useEffect(() => {
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["app-user-data"] }),
      queryClient.invalidateQueries({ queryKey: ["userRelationship"] }),
      queryClient.invalidateQueries({ queryKey: ["deceasedSSNData"] }),
      queryClient.invalidateQueries({ queryKey: ["userSettingsSupabase"] }),
      queryClient.invalidateQueries({ queryKey: ["userIdExtraction"] }),
      queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] }),
    ]).then(() => setIsRefreshing(false));
  }, [queryClient]);

  const dataReady =
    !isRefreshing &&
    !isTaskLoading &&
    !deceasedData.isLoading &&
    !userData.isLoading &&
    !isDeceasedSSNLoading &&
    !isUserSSNLoading &&
    !isRelationshipLoading;

  useEffect(() => {
    if (viewState !== null || !dataReady) return;

    if (estateTask?.status === "completed") {
      setViewState("submitted");
      return;
    }

    const hasEmptyRequired = fields.some((f) => {
      if (!f.required) return false;
      const val = formValues[f.key] ?? "";
      if (f.type === "ssn") return !isValidSSN(val);
      return !val.trim();
    });
    setViewState(hasEmptyRequired ? "editing" : "reviewing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, viewState]);

  const isFieldMissing = useCallback(
    (field: FormField): boolean => {
      if (!field.required) return false;
      const val = formValues[field.key] ?? "";
      if (field.type === "ssn") return !isValidSSN(val);
      return !val.trim();
    },
    [formValues],
  );

  const missingRequiredFields = useMemo(() => {
    if (viewState === "submitted") return [];
    return fields.filter(isFieldMissing);
  }, [fields, isFieldMissing, viewState]);

  const handleFieldChange = useCallback(
    (key: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [setFormValues],
  );

  const handleAddressSelect = useCallback(
    (
      prefix: string,
      value: string,
      placeDetails?: google.maps.places.Place | null,
      subpremise?: string | null,
    ) => {
      const extracted = extractAddressFromPlace(value, placeDetails, subpremise);
      const stateAbbr =
        US_STATES.find((s) => s.label === extracted.state)?.value || extracted.state;

      setFormValues((prev) => ({
        ...prev,
        [`${prefix}_address`]: extracted.streetAddress,
        [`${prefix}_apartment`]: extracted.apartment || prev[`${prefix}_apartment`] || "",
        [`${prefix}_city`]: extracted.city || prev[`${prefix}_city`] || "",
        [`${prefix}_state`]: stateAbbr || prev[`${prefix}_state`] || "",
        [`${prefix}_zip`]: extracted.zipCode || prev[`${prefix}_zip`] || "",
      }));
    },
    [setFormValues],
  );

  const validateFields = useCallback((): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};
    for (const field of fields) {
      if (isFieldMissing(field)) {
        fieldErrors[field.key] = "Required";
      } else if (field.pattern) {
        const val = formValues[field.key]?.trim();
        if (val && !field.pattern.test(val)) {
          fieldErrors[field.key] = field.patternMessage ?? "Invalid format";
        }
      }
    }
    return fieldErrors;
  }, [fields, formValues, isFieldMissing]);

  const handleSaveAndReview = useCallback(() => {
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setViewState("reviewing");
  }, [validateFields]);

  const performSubmit = useCallback(async () => {
    if (!estateId || !taskId) return;

    setIsSubmitting(true);

    try {
      const now = new Date().toISOString();

      // Save deceased data back to persons table
      if (deceasedData.deceasedUserId) {
        const personUpdate: Record<string, unknown> = {};

        if (formValues.date_of_birth) {
          personUpdate.date_of_birth = formValues.date_of_birth;
        }
        if (formValues.date_of_death) {
          personUpdate.date_of_death = formValues.date_of_death;
        }

        const street = formValues.deceased_address?.trim();
        if (street) {
          personUpdate.location = buildAddress({
            streetAddress: street,
            apartment: formValues.deceased_apartment?.trim() || "",
            city: formValues.deceased_city?.trim() || "",
            state: formValues.deceased_state?.trim() || "",
            zipCode: formValues.deceased_zip?.trim() || "",
          });
        }

        if (Object.keys(personUpdate).length > 0) {
          await supabase
            .from("estates")
            .update(personUpdate)
            .eq("estate_id", deceasedData.deceasedUserId);

          const selectedAddressId = formValues.deceased_address_id?.trim() ?? "";

          if (/^\d+$/.test(selectedAddressId)) {
            void resolveAddressLink({
              addressId: Number(selectedAddressId),
              entity: { estate_id: deceasedData.deceasedUserId },
            }).finally(() => {
              void queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });
            });
          } else if (typeof personUpdate.location === "string" && personUpdate.location) {
            void resolveAddressLink({
              raw: personUpdate.location,
              purpose: "domicile",
              entity: { estate_id: deceasedData.deceasedUserId },
            }).finally(() => {
              void queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });
            });
          }
        }

        // Save deceased SSN (encrypted)
        const deceasedSSNDigits = formValues.deceased_ss_number?.replace(/\D/g, "") ?? "";
        if (deceasedSSNDigits.length === 9) {
          await saveDeceasedSSN({
            ssn: deceasedSSNDigits,
            estateId: deceasedData.deceasedUserId,
          });
        }
      }

      // Save user data back to users table
      const userUpdate: Record<string, unknown> = {};
      if (formValues.user_first_name || formValues.user_last_name) {
        const first = formValues.user_first_name?.trim() || "";
        const last = formValues.user_last_name?.trim() || "";
        userUpdate.full_name = `${first} ${last}`.trim();
      }
      if (formValues.user_phone) {
        userUpdate.phone_number = stripPhone(formValues.user_phone);
      }
      if (Object.keys(userUpdate).length > 0) {
        await updateUserAsync({ user: userUpdate });
      }

      // Save relationship to user_estates
      if (formValues.relationship_to_deceased && deceasedData.deceasedUserId && user?.user_id) {
        await supabase
          .from("user_estates")
          .update({ relationship_to_decedent: formValues.relationship_to_deceased })
          .eq("user_id", user.user_id)
          .eq("estate_id", deceasedData.deceasedUserId);
      }

      // Mark estate task as completed
      const { data: existing } = await supabase
        .from("estate_tasks")
        .select("id")
        .eq("estate_id", estateId)
        .eq("task_id", taskId)
        .maybeSingle();

      let estateTaskId: number | undefined;

      const isElayneTask = (ELAYNE_TASK_IDS as readonly string[]).includes(taskId);

      if (existing) {
        estateTaskId = existing.id;
        await supabase
          .from("estate_tasks")
          .update({
            status: "completed",
            completed_at: now,
            last_active_at: now,
            ...(isElayneTask ? { elayne_status: "pending" } : {}),
          })
          .eq("estate_id", estateId)
          .eq("task_id", taskId);
      } else {
        const { data: inserted } = await supabase
          .from("estate_tasks")
          .insert({
            estate_id: estateId,
            task_id: taskId,
            status: "completed",
            started_at: now,
            completed_at: now,
            last_active_at: now,
            ...(isElayneTask ? { elayne_status: "pending" } : {}),
          })
          .select("id")
          .single();
        estateTaskId = inserted?.id;
      }

      const resolvedName = module.name.replace("{{ firstName }}", deceasedData.firstName ?? "");

      if (estateTaskId) {
        await TaskActionLogger.logStatusChange({
          estateId,
          userId: user?.user_id?.toString(),
          taskName: resolvedName,
          oldStatus: existing ? "in_progress" : "not_started",
          newStatus: "completed",
          changedBy: "user",
        });
      }

      if (isElayneTask && estateId && user?.user_id) {
        logActivity({
          estate_id: estateId,
          action_type: "submitted_elayne_task",
          user_id: user.user_id,
          entity_type: "task",
          entity_id: taskId,
          metadata: {
            task_id: taskId,
            task_name: resolvedName,
          },
        }).catch((err) => console.warn("logActivity failed:", err));
      }

      await queryClient.invalidateQueries({
        queryKey: ["estateTask", estateId, taskId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["moduleProgress", estateId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["elayneTasks", estateId],
      });

      const authEmail = (await supabase.auth.getUser()).data.user?.email;
      if (isElayneTask && canSendNotification(authEmail) && estateId) {
        notifyTaskSubmitted({
          taskName: resolvedName,
          taskId,
          estateId,
        }).catch((err) => console.warn("notifyTaskSubmitted failed:", err));
      }

      trackEvent(EventType.ESSENTIALS_INLINE_SUBMITTED, {
        module_id: module.id,
        task_id: taskId,
      });
      setViewState("submitted");
    } catch (error) {
      console.error("Failed to submit form:", error);
      enqueueSnackbar(TOAST.GENERIC_ERROR, {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    estateId,
    taskId,
    formValues,
    deceasedData.deceasedUserId,
    saveDeceasedSSN,
    updateUserAsync,
    queryClient,
    module.name,
    user,
  ]);

  const handleSubmitClick = useCallback(() => {
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setViewState("editing");
      return;
    }

    if (!userData.completedAuthorization) {
      setShowTPA(true);
      return;
    }

    performSubmit();
  }, [validateFields, userData.completedAuthorization, performSubmit]);

  return {
    viewState,
    setViewState,
    fields,
    formValues,
    errors,
    ssnVisible,
    setSSNVisible,
    isSubmitting,
    showTPA,
    setShowTPA,
    missingRequiredFields,
    deceasedFirstName: deceasedData.displayName,
    handleFieldChange,
    handleAddressSelect,
    handleSaveAndReview,
    handleSubmitClick,
    performSubmit,
  };
}
